import axios from '../api/axios';

/**
 * Public API functions for Quick Loan Application with Referrer Tracking
 * These functions don't require authentication but need API key
 */

// API Key for public endpoints - matches backend validation
const API_KEY = process.env.REACT_APP_API_KEY;

// Common headers for all API requests
const getApiHeaders = () => ({
    'api-key': API_KEY,
    'Content-Type': 'application/json'
});

//GET REFERRER DETAILS BY SHAREABLE LINK UUID AND INCREMENT CLICK COUNT
const getShareableLinkDetails = async (uuid) => {
    try {
        const response = await axios.get(`public/shareable-link/${uuid}`, {
            headers: getApiHeaders()
        });
        return { 
            error: 0, 
            httpCode: response.status, 
            message: "Referrer details retrieved successfully", 
            data: response.data.data || {} 
        };
    } catch (e) {
        if (e.response) {
            return { 
                error: 1, 
                httpCode: e.response.status, 
                message: e.response.data?.message || "Failed to retrieve referrer details", 
                data: {} 
            };
        }
        return { 
            error: 1, 
            httpCode: 500, 
            message: "Network error occurred", 
            data: {} 
        };
    }
};

//VERIFY SHAREABLE LINK WITHOUT INCREMENTING CLICK COUNT
const verifyShareableLink = async (uuid) => {
    try {
        const response = await axios.get(`public/verify-link/${uuid}`, {
            headers: getApiHeaders()
        });
        return { 
            error: 0, 
            httpCode: response.status, 
            message: "Link verified successfully", 
            data: response.data.data || {} 
        };
    } catch (e) {
        if (e.response) {
            return { 
                error: 1, 
                httpCode: e.response.status, 
                message: e.response.data?.message || "Link verification failed", 
                data: {} 
            };
        }
        return { 
            error: 1, 
            httpCode: 500, 
            message: "Network error occurred", 
            data: {} 
        };
    }
};

//CREATE QUICK LOAN APPLICATION WITH REFERRER TRACKING
const createQuickLoanApplication = async (applicationData, referrerUuid = null) => {
    try {
        // Prepare the payload with proper structure - separate application and applicant data
        const payload = {
            application: {
                // APPLICATION-LEVEL DATA (loan details, financial info, etc.)
                loan: {
                    purpose: applicationData.loanDetail?.purpose || '',
                    amount: applicationData.loanDetail?.amount || '',
                    term: applicationData.loanDetail?.term || '',
                    type: 'consumer' // Default for quick loans
                },
                financial: {
                    assetPrice: '',
                    cashDeposit: '',
                    // Keep minimal for quick application
                },
                buyingItem: {
                    motorVehicles: []
                },
                repayments: {
                    repaymentType: '',
                    includingInsurance: { weekly: 0, fortnightly: 0, monthly: 0 },
                    excludingInsurance: { weekly: 0, fortnightly: 0, monthly: 0 }
                },
                insurances: [],
                submittedAt: new Date().toISOString(),
                source: 'quick-loan-application',
                category: 'individual'
            },
            applicant: {
                // APPLICANT-LEVEL DATA (personal details, contact info, etc.)
                personalDetail: {
                    title: applicationData.personalDetail?.title || '',
                    firstName: applicationData.personalDetail?.firstName || '',
                    middleName: applicationData.personalDetail?.middleName || '',
                    surname: applicationData.personalDetail?.surname || '',
                    dob: applicationData.personalDetail?.dateOfBirth || '',
                    email: applicationData.personalDetail?.email || '',
                    mobileNumber: applicationData.personalDetail?.mobileNumber || '',
                    homeNumber: applicationData.personalDetail?.homeNumber || '',
                    residentialHistory: {
                        current: {
                            address: applicationData.personalDetail?.residentialAddress || {},
                            duration: 0,
                            status: '',
                            landlord: { name: '' }
                        },
                        previous: []
                    },
                    bestContactMethod: applicationData.contactPreferences?.preferredContactMethod || 'Phone',
                    notes: applicationData.additionalInfo?.notes || ''
                },
                income: {
                    primary: [], // Keep empty for quick application
                    secondary: [],
                    previous: [],
                    notes: ''
                },
                assets: [], // Keep empty for quick application
                debts: [],  // Keep empty for quick application
                expenses: [] // Keep empty for quick application
            },
            referrerUuid: referrerUuid,
            // Store original quick form data for reference
            quickFormData: {
                contactPreferences: applicationData.contactPreferences || {},
                additionalInfo: applicationData.additionalInfo || {}
            }
        };

        const response = await axios.post(`public/application/0/0`, payload, { 
            headers: getApiHeaders()
        });

        return { 
            error: 0, 
            httpCode: response.status, 
            message: "Application created successfully", 
            data: response.data,
            token: response.data.token,
            applicationId: response.data.applicationId,
            referrerInfo: response.data.referrerInfo
        };
    } catch (e) {
        if (e.response) {
            return { 
                error: 1, 
                httpCode: e.response.status, 
                message: e.response.data?.message || "Failed to create application", 
                data: {} 
            };
        }
        return { 
            error: 1, 
            httpCode: 500, 
            message: "Network error occurred", 
            data: {} 
        };
    }
};

//MARK APPLICATION AS RECEIVED FOR REFERRER TRACKING
const markApplicationReceived = async (uuid, applicationId) => {
    try {
        const response = await axios.post(`public/mark-application/${uuid}`, {
            applicationId: applicationId
        }, {
            headers: getApiHeaders()
        });

        return { 
            error: 0, 
            httpCode: response.status, 
            message: "Application marked as received successfully" 
        };
    } catch (e) {
        if (e.response) {
            return { 
                error: 1, 
                httpCode: e.response.status, 
                message: e.response.data?.message || "Failed to mark application as received" 
            };
        }
        return { 
            error: 1, 
            httpCode: 500, 
            message: "Network error occurred" 
        };
    }
};

//SUBMIT COMPLETE LOAN APPLICATION WITH REFERRER TRACKING (ONE-STEP PROCESS)
const submitQuickLoanApplicationWithReferrer = async (formData, referrerUuid = null) => {
    try {
        // Step 1: Create application
        const createResponse = await createQuickLoanApplication(formData, referrerUuid);
        
        if (createResponse.error === 1) {
            return createResponse;
        }

        // Step 2: Mark application as received for referrer tracking (if referrer exists)
        if (referrerUuid && createResponse.applicationId) {
            try {
                await markApplicationReceived(referrerUuid, createResponse.applicationId);
            } catch (trackingError) {
                console.warn('Referrer tracking failed but application was created successfully:', trackingError);
                // Don't fail the entire process if tracking fails
            }
        }

        return {
            error: 0,
            httpCode: createResponse.httpCode,
            message: "Application submitted successfully",
            data: createResponse.data,
            token: createResponse.token,
            applicationId: createResponse.applicationId,
            referrerInfo: createResponse.referrerInfo
        };

    } catch (e) {
        console.error('Error in submitQuickLoanApplicationWithReferrer:', e);
        return { 
            error: 1, 
            httpCode: 500, 
            message: "Failed to submit application", 
            data: {} 
        };
    }
};

//GET REFERRER INFO FROM URL PARAMETERS
const extractReferrerFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('ref');
};

//HELPER FUNCTION TO VALIDATE REFERRER LINK BEFORE FORM SUBMISSION
const validateReferrerLink = async (uuid) => {
    if (!uuid) {
        return { isValid: false, message: "No referrer UUID provided" };
    }

    try {
        const response = await verifyShareableLink(uuid);
        
        if (response.error === 0) {
            return { 
                isValid: true, 
                message: "Valid referrer link", 
                data: response.data 
            };
        } else {
            return { 
                isValid: false, 
                message: response.message,
                data: {} 
            };
        }
    } catch (error) {
        return { 
            isValid: false, 
            message: "Failed to validate referrer link",
            data: {} 
        };
    }
};

//GET APPLICATION STATUS (if token is available)
const getQuickApplicationStatus = async (applicationId, applicantId, token) => {
    try {
        const response = await axios.get(`public/application/${applicationId}/${applicantId}`, { 
            headers: { 
                ...getApiHeaders(),
                Authorization: "Bearer " + token
            } 
        });

        return { 
            error: 0, 
            httpCode: response.status, 
            message: "Application status retrieved successfully", 
            data: response.data || {} 
        };
    } catch (e) {
        if (e.response) {
            return { 
                error: 1, 
                httpCode: e.response.status, 
                message: e.response.data?.message || "Failed to retrieve application status", 
                data: {} 
            };
        }
        return { 
            error: 1, 
            httpCode: 500, 
            message: "Network error occurred", 
            data: {} 
        };
    }
};

export { 
    getShareableLinkDetails,
    verifyShareableLink,
    createQuickLoanApplication,
    markApplicationReceived,
    submitQuickLoanApplicationWithReferrer,
    extractReferrerFromURL,
    validateReferrerLink,
    getQuickApplicationStatus
};